import React, { useCallback } from "react";
import PathPickMobileFilterModal from "@/features/pathpick/components/ui/PathPickMobileFilterModal";
import PathPickRangeFilterSection from "@/features/pathpick/components/ui/PathPickRangeFilterSection";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import usePathPickBasketFilter, {
  PathPickBasketFilter,
  PathPickBasketFilterKey,
} from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import usePathPickBasketFilterGroups, {
  getPathPickBasketFilterValues,
  togglePathPickBasketFilterValue,
} from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilterGroups";
import usePathPickFilterDraft from "@/features/pathpick/hooks/usePathPickFilterDraft";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import { BASKET_SORT_LABEL_KEYS } from "@/features/pathpick/features/basket/constants";
import { PathPickMarketPriceBasis } from "@/features/pathpick/types";
import {
  CLEARED_PATH_PICK_RANGE_FILTER,
  getInvalidPathPickRangeFields,
  hasPathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 모달에서 고르는 그룹 키 — 초기화 대상이자 배지 카운트 대상이다.
const BASKET_MODAL_GROUP_KEYS: PathPickBasketFilterKey[] = [
  "brandId__in",
  "pathradeCategory__in",
  "productType__in",
  "requestStatus__in",
  "requestChannel__in",
  "soldOut",
];

const DEFAULT_MARKET_PRICE_BASIS: PathPickMarketPriceBasis = "bidPrice";

// 모바일 필터 버튼 배지 — 기본값이 아닌 필터 축의 수(모달 안에서 고를 수 있는 축만 센다).
export const countPathPickBasketFilterAxes = (
  filter: PathPickBasketFilter,
  {
    isDefaultSort,
    marketPriceBasis,
    isComparisonVisible,
  }: {
    isDefaultSort: boolean;
    marketPriceBasis: PathPickMarketPriceBasis;
    isComparisonVisible: boolean;
  }
) =>
  [
    ...BASKET_MODAL_GROUP_KEYS.map(
      (key) => getPathPickBasketFilterValues(filter, key).length > 0
    ),
    hasPathPickRangeFilter(filter),
    !isDefaultSort,
    isComparisonVisible && marketPriceBasis !== DEFAULT_MARKET_PRICE_BASIS,
  ].filter(Boolean).length;

// 담은 상품 모바일 필터 — 데스크톱 헤더의 비교 기준·정렬·필터 패널을 한 모달에 모은다.
// 정렬·비교 기준까지 전부 드래프트로 들고 있다가 `필터 적용`에서 한 번에 반영한다.
// 열 때만 마운트되므로 드래프트는 열 때마다 적용값에서 다시 시작한다.
function PathPickBasketListFilterModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();
  const { filter, sort, sortOptions, marketPriceBasis, apply, changeSort } =
    usePathPickBasketFilter();
  const groups = usePathPickBasketFilterGroups();
  // 범위 필터는 크림·포이즌 비교 값으로 거르는 조건이라 비교 표시를 끄면 함께 감춘다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const { draft, setDraftValues, updateDraft } =
    usePathPickFilterDraft<PathPickBasketFilter>(filter);

  // 정렬·비교 기준의 디폴트는 URL에서 생략되므로 훅과 같은 규칙으로 폴백한다.
  // (탭의 정렬 옵션에 없는 값 — 비교 표시를 끈 뒤의 마진 정렬 등 — 은 탭 디폴트로 본다)
  const sortValue =
    draft.sort && sortOptions.includes(draft.sort)
      ? draft.sort
      : sortOptions[0];
  const basisValue = draft.marketPriceBasis ?? DEFAULT_MARKET_PRICE_BASIS;

  const sortSelectOptions = sortOptions.map((option) => ({
    name: t(BASKET_SORT_LABEL_KEYS[option]),
    value: option,
  }));

  const handleToggle = useCallback(
    (key: PathPickBasketFilterKey, value: string | number) => {
      updateDraft((current) =>
        togglePathPickBasketFilterValue(current, key, value)
      );
    },
    [updateDraft]
  );

  // 탭·검색어는 모달 밖의 값이라 그대로 두고, 모달에서 고르는 축만 기본값으로 되돌린다.
  const handleReset = () => {
    setDraftValues({
      ...BASKET_MODAL_GROUP_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: undefined }),
        {} as PathPickBasketFilter
      ),
      ...CLEARED_PATH_PICK_RANGE_FILTER,
      sort: undefined,
      marketPriceBasis: undefined,
    });
  };

  // 바뀐 게 없으면 닫기만 한다 — apply는 항상 1페이지로 되돌려 보던 페이지를 잃는다.
  // 디폴트 생략 여부로 다르게 보이지 않도록 폴백이 채워진 값끼리 비교한다.
  const handleApply = () => {
    const isChanged =
      JSON.stringify({
        ...draft,
        sort: sortValue,
        marketPriceBasis: basisValue,
      }) !== JSON.stringify({ ...filter, sort, marketPriceBasis });
    if (isChanged) {
      apply({
        ...draft,
        sort: sortValue,
        marketPriceBasis:
          basisValue === DEFAULT_MARKET_PRICE_BASIS ? undefined : basisValue,
      });
    }
    onClose();
  };

  return (
    <PathPickMobileFilterModal
      isOpen={isOpen}
      onClose={onClose}
      sortOptions={sortSelectOptions}
      sortValue={sortValue}
      onChangeSort={(next) => setDraftValues({ sort: next })}
      marketPriceBasis={basisValue}
      onChangeMarketPriceBasis={(next) =>
        setDraftValues({ marketPriceBasis: next })
      }
      // 비교 표시를 끄면 마진·거래 정렬이 사라지므로, URL에 남은 정렬은 탭 디폴트로 되돌린다(데스크톱과 같다).
      onComparisonHidden={() => changeSort(undefined)}
      groups={groups}
      getSelectedValues={(key) => getPathPickBasketFilterValues(draft, key)}
      onToggle={handleToggle}
      extraCells={
        isComparisonVisible ? (
          <PathPickRangeFilterSection value={draft} onChange={setDraftValues} />
        ) : undefined
      }
      onReset={handleReset}
      onApply={handleApply}
      isApplyDisabled={getInvalidPathPickRangeFields(draft).length > 0}
    />
  );
}

export default PathPickBasketListFilterModal;
